import React, { useEffect, useState, useCallback, useMemo } from 'react'
import AdminLeftMenu from './backend/AdminLeftMenu';
import { useOutletContext,Link } from "react-router-dom";
import PractitionerService from '../services/practitionerService';
import FacilityService from '../services/facilityService';
import centralAppointmentService from '../services/centralAppointmentService';
import { toast } from 'react-toastify';
import AdminChart from './AdminChart';
import notificationService from '../services/notificationService';
import '../components/NotificationStyles.css';

const Admin = (props) => {
    const [userData, currentUser] = useOutletContext();
    const [items, setItem] = useState([]);
    const [upcomingAppointments, setUpcomingAppointments] = useState([]);
    const [monthlyChartData, setMonthlyChartData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedPhysician, setSelectedPhysician] = useState(null);
    const [isRemoving, setIsRemoving] = useState(false);
    const [requestedFacilities, setRequestedFacilities] = useState([]);
    const practitionerService = useMemo(() => new PractitionerService(), []);
    const facilityService = useMemo(() => new FacilityService(), []);

    const getAdminList = useCallback(async () => {
        if (!currentUser?.uid) {
            // If no user logged in, show empty state
            setItem([]);
            return;
        }

        try {
            setIsLoading(true);
            // Get real saved practitioners from Firebase
            const savedPractitioners = await practitionerService.getSavedPractitioners(currentUser.uid);
        
                        if (savedPractitioners && savedPractitioners.length > 0) {
                // 🚀 Check if migration is needed (existing practitioners but no appointments)
                const existingAppointments = await practitionerService.getSavedAppointments(currentUser.uid);
                
                if (!existingAppointments || existingAppointments.length === 0) {
                    console.log('🔄 No appointments found - running automatic migration...');
                    const migrationResult = await practitionerService.migrateExistingPractitioners(currentUser.uid);
                    console.log('🎉 Migration result:', migrationResult);
                    
                    if (migrationResult.success && migrationResult.migratedCount > 0) {
                        toast.success(`Migrated appointments for ${migrationResult.migratedCount} practitioners!`);
                    }
                } else {
                    // 🚀 Check for FHIR object data that needs cleaning
                    console.log('🔍 Checking for FHIR object data issues...');
                    const dataCleanupResult = await practitionerService.migrateFirebaseAppointmentData(currentUser.uid);
                    
                    if (dataCleanupResult.success && dataCleanupResult.migratedCount > 0) {
                        console.log(`🧹 Cleaned up ${dataCleanupResult.migratedCount} appointment records`);
                        toast.info(`Fixed facility data for ${dataCleanupResult.migratedCount} appointments`);
                    }
                }
                
                // Use centralized service for all appointment data WITH userId for Firebase appointments
                const [dashboardData, upcomingAppointments, chartData] = await Promise.all([
                    centralAppointmentService.getDashboardData(savedPractitioners, currentUser.uid),
                    centralAppointmentService.getUpcomingAppointments(savedPractitioners, currentUser.uid),
                    centralAppointmentService.getMonthlyChartData(savedPractitioners, currentUser.uid)
                ]);

                setItem(dashboardData);
                setUpcomingAppointments(upcomingAppointments);
                setMonthlyChartData(chartData);
            } else {
                // No saved practitioners - show empty state
                setItem([]);
                setUpcomingAppointments([]);
                setMonthlyChartData([]);
            }

            // Load requested facilities
            const facilities = await facilityService.getUserFacilityRequests(currentUser.uid);
            setRequestedFacilities(facilities);
        } catch (error) {
            console.error('Error fetching saved practitioners:', error);
            setItem([]);
            setUpcomingAppointments([]);
            setMonthlyChartData([]);
        } finally {
            setIsLoading(false);
        }
    }, [currentUser?.uid, practitionerService, facilityService]); // Add service dependencies

    const handleRefreshData = async () => {
        try {
            setIsLoading(true);
            toast.info('Refreshing facility information...');
            
            // Clear cache to force fresh data fetch
            centralAppointmentService.clearCache();
            
            // Step 1: Migrate existing appointment data to fix FHIR objects
            if (currentUser?.uid) {
                console.log('🔄 Running appointment data migration...');
                const migrationResult = await practitionerService.migrateFirebaseAppointmentData(currentUser.uid);
                
                if (migrationResult.success && migrationResult.migratedCount > 0) {
                    toast.success(`Fixed ${migrationResult.migratedCount} appointment records!`);
                }
                
                // Step 2: Refresh appointments for all practitioners
                await practitionerService.refreshAllAppointments(currentUser.uid);
                toast.success('Facility information updated!');
            }
            
            // Step 3: Reload the admin list with fresh data
            await getAdminList();
        } catch (error) {
            console.error('Error refreshing data:', error);
            toast.error('Failed to refresh data. Please try again.');
        }
    };

    const handleRemovePhysician = async () => {
        if (!selectedPhysician) {
            toast.error('No physician selected.');
            return;
        }

        try {
            setIsRemoving(true);
            
            // Use the Firestore document ID to remove the physician
            const firestoreDocId = selectedPhysician.id;
            if (!firestoreDocId) {
                toast.error('Unable to identify physician to remove.');
                return;
            }

            const result = await practitionerService.removePractitioner(firestoreDocId);
            
            if (!result.success) {
                toast.error(result.message || 'Failed to remove physician.');
                return;
            }
            
            toast.success(`${selectedPhysician.full_name} has been removed from your dashboard and calendar.`);
            
            // Refresh the list and upcoming schedules
            await getAdminList();
            
            // Close modal programmatically
            const modal = document.getElementById('removePhysicianModal');
            if (modal) {
                const bootstrapModal = window.bootstrap?.Modal?.getInstance(modal);
                if (bootstrapModal) {
                    bootstrapModal.hide();
                } else {
                    // Fallback: hide modal manually
                    modal.style.display = 'none';
                    modal.classList.remove('show');
                    document.body.classList.remove('modal-open');
                    
                    // Remove backdrop
                    const backdrop = document.querySelector('.modal-backdrop');
                    if (backdrop) {
                        backdrop.remove();
                    }
                }
            }
            
            // Reset state
            setSelectedPhysician(null);
            
        } catch (error) {
            console.error('Error removing physician:', error);
            toast.error('Failed to remove physician. Please try again.');
        } finally {
            setIsRemoving(false);
        }
    };

    const openRemoveModal = (physician) => {
        setSelectedPhysician(physician);
    };

    const removeFacility = async (facilityId) => {
        if (!currentUser?.uid) {
            toast.error('Please log in to remove facilities');
            return;
        }

        if (!window.confirm('Are you sure you want to remove this facility request?')) {
            return;
        }

        try {
            setIsRemoving(true);
            
            // Remove from Firebase
            await facilityService.removeFacilityRequest(facilityId);
            
            // Update local state
            setRequestedFacilities(prev => prev.filter(facility => facility.id !== facilityId));
            
            toast.success('Facility request removed successfully');
            
        } catch (error) {
            console.error('Error removing facility:', error);
            toast.error('Failed to remove facility request. Please try again.');
        } finally {
            setIsRemoving(false);
        }
    };

    const closeRemoveModal = () => {
        setSelectedPhysician(null);
        setIsRemoving(false);
    };

    const scrollToUpcomingSchedules = () => {
        const upcomingSection = document.getElementById('upcoming-schedules-section');
        if (upcomingSection) {
            upcomingSection.scrollIntoView({ 
                behavior: 'smooth',
                block: 'start'
            });
        }
    };

    useEffect(() => {
        getAdminList();
        
        // Initialize HL7 FHIR notification service
        const initializeNotifications = async () => {
            await notificationService.initialize();
            await notificationService.requestPermission();
            
            // Subscribe to admin dashboard refresh notifications
            notificationService.subscribe('refresh_admin', (data) => {
                console.log('🔔 Admin dashboard refresh triggered:', data);
                getAdminList(); // Refresh admin data when Epic FHIR data updates
            });
            
            // Subscribe to Epic FHIR data refresh notifications
            notificationService.subscribe('refresh_all', (data) => {
                console.log('🔔 Epic FHIR data refresh triggered:', data);
                getAdminList(); // Refresh all data when Epic updates
            });
        };
        
        initializeNotifications();
        
        return () => {
            console.log("cleanup");
            // Clean up notification subscriptions
            notificationService.disconnect();
        }
    }, [getAdminList]);

    return (
        <>
            <div className="offcanvas offcanvas-start" id="demo">
                <AdminLeftMenu />
            </div>
            <div className="rightpane">
                <div className="container">
                    <div className="welcombox">
                        <div className="row align-items-center">
                            <div className="col-md-6">
                                <h1 className="mt-4">Hey {(userData) ? userData.name : ''}, Good Morning <img src="/images/sunimg.png" alt="imgess" /></h1>
                                <h2>Welcome to your Replinq dashboard. View case insights below, edit your physicians and preview your next upcoming case.
                                </h2>
                                <h3>
                                    <i className="fa fa-calendar"></i> {new Date().toLocaleDateString('en-US', { 
                                        weekday: 'long', 
                                        year: 'numeric', 
                                        month: 'long', 
                                        day: 'numeric' 
                                    })}, You got{' '}
                                    <span 
                                        onClick={scrollToUpcomingSchedules}
                                        style={{
                                            color: '#E07A55',
                                            cursor: 'pointer',
                                            textDecoration: 'underline'
                                        }}
                                        title="Click to view upcoming schedules"
                                    >
                                        {upcomingAppointments.length} upcoming schedule{upcomingAppointments.length !== 1 ? 's' : ''}
                                    </span>
                                </h3>
                            </div>
                            <div className="col-md-6 text-end">
                                <img src="/images/welcomeImg.png" alt="img" />
                            </div>
                        </div>
                    </div>
                </div>
                <div className='container'>
                    <div className='row'>
                        <div className='col-md-7'>
                            <div className="row align-items-center">
                                <div className="col-md-12">
                                    <div className="schedule-boxleft">
                                        <div className="yearbox">
                                            <h2>My Physicians ({items.length})</h2>
                                            <span>
                                                <Link to="/physicians" className="me-3 text-dark addphy">
                                                    <i className="fa fa-plus"></i>
                                                    <b className='d-none d-lg-inline'>Add Physicians</b>
                                                </Link>
                                                <button 
                                                    onClick={handleRefreshData}
                                                    className="btn btn-link text-dark p-0 me-2"
                                                    disabled={isLoading}
                                                    title="Refresh facility information"
                                                >
                                                    <i className={`fa ${isLoading ? 'fa-spinner fa-spin' : 'fa-refresh'}`}></i>
                                                    <b className='d-none d-lg-inline ms-1'>Refresh</b>
                                                </button>
                                            </span>
                                        </div>
                                        
                                        {/* Interactive Admin Chart - replaces placeholder image */}
                                        <AdminChart 
                                            monthlyData={monthlyChartData} 
                                            isLoading={isLoading} 
                                        />
                                        
                                        <div className="yearboxfooter">
                                            <span></span> Physicians
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="row align-items-center" id="upcoming-schedules-section">
                                <div className="col-md-12">
                                    <div className="schedule-boxleft">
                                        <div className="yearbox">
                                            <h2>
                                                {upcomingAppointments.length > 0 ? (
                                                    <>
                                                        Next Day Schedule - {new Date(upcomingAppointments[0].start_date).toLocaleDateString('en-US', { 
                                                            weekday: 'long', 
                                                            month: 'short', 
                                                            day: 'numeric' 
                                                        })} ({upcomingAppointments.length})
                                                    </>
                                                ) : (
                                                    'Upcoming Schedules (0)'
                                                )}
                                            </h2>
                                        </div>
                                        {isLoading ? (
                                            <div className="text-center p-4">
                                                <div className="loading-spinner-container">
                                                    <div className="loading-spinner"></div>
                                                    <p>Loading upcoming schedules...</p>
                                                </div>
                                            </div>
                                        ) : upcomingAppointments.length > 0 ? (
                                            upcomingAppointments.map((appointment, index) => (
                                                <div key={appointment.id} className="upschedulebox bdrleft mb-3">
                                            <div className="uphdr">
                                                <div className="dot-box"></div>
                                                        <div className="dr-name p-0">
                                                            <b>{appointment.full_name}</b>
                                                            <span>({appointment.specialty})</span>
                                                        </div>
                                                        <div className="upcom-time ms-auto">
                                                            {appointment.start_time} - {appointment.end_time}
                                                        </div>
                                            </div>
                                            <div className="myphy-boxleft">
                                                <div className='row'>
                                                    <div className='col-1 text-center mb-3'><i className="fa fa-building"></i></div>
                                                            <div className='col-11 mb-3 ps-0'>
                                                                {appointment.location || appointment.hospital || appointment.organizationName || 'Unknown'}
                                                            </div>
                                                            <div className='col-1 text-center mb-2'><i className="fa fa-calendar"></i></div>
                                                            <div className='col-11 ps-0 mb-2'>
                                                                <strong>{new Date(appointment.start_date).toLocaleDateString('en-US', { 
                                                                    weekday: 'long', 
                                                                    year: 'numeric', 
                                                                    month: 'long', 
                                                                    day: 'numeric' 
                                                                })}</strong>
                                                            </div>
                                                    <div className='col-1 text-center'><i className="fa fa-medkit"></i></div>
                                                            <div className='col-11 ps-0'>
                                                                {appointment.procedure || appointment.description || 'Unknown Procedure'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="text-center p-4">
                                                <div className="py-3">
                                                    <i className="fa fa-calendar-o fa-3x text-muted mb-3"></i>
                                                    <h5>No Upcoming Schedules</h5>
                                                    <p className="text-muted">
                                                        {items.length === 0 ? 
                                                            "Add physicians to see their upcoming appointments." :
                                                            "No future appointments scheduled."
                                                        }
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='col-md-5'>
                            <div className="schedule-boxleft">
                                <div className="yearbox">
                                    <h2>Yearly Schedules Insight</h2>
                                    <span>
                                        <Link to="/physicians" className="me-4 text-dark addphy">
                                            <i className="fa fa-plus"></i>
                                            <b className='d-none d-lg-inline'>Add Physicians</b>
                                        </Link>
                                    </span>
                                </div>

                                <ul className="list-group addphylist">
                                    {isLoading ? (
                                        <li className="list-group-item text-center">
                                            <div className="loading-spinner-container">
                                                <div className="loading-spinner"></div>
                                                <p>Loading physicians...</p>
                                            </div>
                                        </li>
                                    ) : items && Array.isArray(items) && items.length > 0 ? items.map((employee, index) => {
                                            return (
                                                <React.Fragment key={employee.id || index}>
                                                    <li className="list-group-item d-flex justify-content-between align-items-top">
                                                    <div>
                                                        <b><small className='dotdr orng'></small> {employee.full_name}</b>
                                                        <span className='drspec d-block'>{employee.specialty}</span>
                                                        {employee.npi && (
                                                            <small className="text-muted d-block mt-1" style={{marginLeft: '20px'}}>NPI: {employee.npi}</small>
                                                        )}
                                                    </div>
                                                    <div className="text-end">
                                                        <button 
                                                            type="button" 
                                                            className="btn p-0"
                                                            onClick={() => openRemoveModal(employee)}
                                                            data-bs-toggle="modal" 
                                                            data-bs-target="#removePhysicianModal"
                                                        >
                                                            <span className="badge"><i className="fa fa-pencil"></i></span>
                                                        </button>
                                                        <div className="mt-1">
                                                            <small className="text-muted">Cases: {employee.cases_completed}</small>
                                                        </div>
                                                    </div>
                                                    </li>
                                                    <li className='list-group-item'>
                                                        <div className='facilitybox'>
                                                            <span><i className='fa fa-building'></i></span>
                                                        <span>Facility - {employee.hospital}</span>
                                                    </div>
                                                    <div className='facilitybox mt-2'>
                                                        <span><i className='fa fa-calendar'></i></span>
                                                        <span>Next Appointment: {employee.next_appointment ? new Date(employee.next_appointment).toLocaleDateString() : 'None scheduled'}</span>
                                                        </div>
                                                    </li>
                                                </React.Fragment>
                                            )
                                        }) : (
                                        <li className="list-group-item text-center">
                                            <div className="py-4">
                                                <i className="fa fa-user-md fa-3x text-muted mb-3"></i>
                                                <h5>No Physicians Added Yet</h5>
                                                <p className="text-muted">Start by searching and adding physicians to see them here.</p>
                                                <Link to="/physicians" className="btn btn-primary">
                                                    <i className="fa fa-plus me-2"></i>Add Your First Physician
                                                </Link>
                                            </div>
                                            </li>
                                    )}
                                </ul>
                            </div>
                            
                            {/* Requested Facilities Section */}
                            <div className="schedule-boxleft mt-4">
                                <div className="yearbox">
                                    <h2>Requested Facilities ({requestedFacilities.length})</h2>
                                    <span>
                                        <Link to="/facilities" className="me-4 text-dark addphy">
                                            <i className="fa fa-plus"></i>
                                            <b className='d-none d-lg-inline'>Request Facilities</b>
                                        </Link>
                                    </span>
                                </div>

                                <ul className="list-group addphylist">
                                    {isLoading ? (
                                        <li className="list-group-item text-center">
                                            <div className="loading-spinner-container">
                                                <div className="loading-spinner"></div>
                                                <p>Loading facilities...</p>
                                            </div>
                                        </li>
                                    ) : requestedFacilities && Array.isArray(requestedFacilities) && requestedFacilities.length > 0 ? requestedFacilities.map((facility, index) => {
                                        return (
                                            <React.Fragment key={facility.id || index}>
                                                <li className="list-group-item d-flex justify-content-between align-items-top">
                                                    <div>
                                                        <b><small className='dotdr blue'></small> {facility.name}</b>
                                                        <span className='drspec d-block'>{facility.type}</span>
                                                        {/* NPI hidden for now */}
                                                    </div>
                                                    <div className="text-end d-flex align-items-center justify-content-end">
                                                        <div className="me-3">
                                                            <small className={`text-${facility.status === 'pending' ? 'warning' : facility.status === 'approved' ? 'success' : 'muted'}`}>
                                                                Status: {facility.status || 'pending'}
                                                            </small>
                                                        </div>
                                                        <button 
                                                            className="btn btn-sm text-danger p-0"
                                                            onClick={() => removeFacility(facility.id)}
                                                            disabled={isRemoving}
                                                            title="Remove facility request"
                                                            style={{border: 'none', background: 'none'}}
                                                        >
                                                            {isRemoving ? (
                                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                            ) : (
                                                                <i className="fa fa-trash"></i>
                                                            )}
                                                        </button>
                                                    </div>
                                                </li>
                                                <li className='list-group-item'>
                                                    {/* Contact Information */}
                                                    <div className='row' style={{marginLeft: '20px'}}>
                                                        {/* Address */}
                                                        {facility.address && 
                                                         facility.address !== 'Address not available' && 
                                                         !String(facility.address).includes('Unknown') && 
                                                         !String(facility.address).includes('00000') && (
                                                            <div className='col-12 mb-2'>
                                                                <i className="fa fa-map-marker text-muted me-2"></i>
                                                                <small>
                                                                    {typeof facility.address === 'string' 
                                                                        ? facility.address 
                                                                        : facility.address.line?.[0] 
                                                                            ? `${facility.address.line[0]}, ${facility.address.city || ''}, ${facility.address.state || ''} ${facility.address.postalCode || ''}`.trim()
                                                                            : 'Address not available'
                                                                    }
                                                                </small>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Phone */}
                                                        {facility.phone && 
                                                         facility.phone !== 'Phone not available' && 
                                                         facility.phone !== '(000) 000-0000' && (
                                                            <div className='col-12 mb-2'>
                                                                <i className="fa fa-phone text-muted me-2"></i>
                                                                <a href={`tel:${facility.phone}`} className="text-decoration-none">
                                                                    <small>{facility.phone}</small>
                                                                </a>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Email */}
                                                        {facility.email && 
                                                         facility.email !== 'Email not available' && (
                                                            <div className='col-12 mb-2'>
                                                                <i className="fa fa-envelope text-muted me-2"></i>
                                                                <a href={`mailto:${facility.email}`} className="text-decoration-none">
                                                                    <small>{facility.email}</small>
                                                                </a>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Website */}
                                                        {facility.website && 
                                                         facility.website !== 'Website not available' && (
                                                            <div className='col-12 mb-2'>
                                                                <i className="fa fa-globe text-muted me-2"></i>
                                                                <a href={facility.website} target="_blank" rel="noopener noreferrer" className="text-decoration-none" style={{wordBreak: 'break-all'}}>
                                                                    <small>
                                                                        {typeof facility.website === 'string' 
                                                                            ? facility.website 
                                                                            : facility.website.url || facility.website
                                                                        }
                                                                    </small>
                                                                </a>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Show message if no contact info available */}
                                                        {(!facility.address || 
                                                          facility.address === 'Address not available' || 
                                                          String(facility.address).includes('Unknown') || 
                                                          String(facility.address).includes('00000')) && 
                                                         (!facility.phone || 
                                                          facility.phone === 'Phone not available' || 
                                                          facility.phone === '(000) 000-0000') && 
                                                         (!facility.email || facility.email === 'Email not available') && 
                                                         (!facility.website || facility.website === 'Website not available') && (
                                                            <div className='col-12'>
                                                                <div className='text-muted'>
                                                                    <small>Contact information available upon request</small>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </li>
                                            </React.Fragment>
                                        )
                                    }) : (
                                        <li className="list-group-item text-center">
                                            <div className="py-4">
                                                <i className="fa fa-hospital-o fa-3x text-muted mb-3"></i>
                                                <h5>No Facilities Requested Yet</h5>
                                                <p className="text-muted">Start by searching and requesting facilities to see them here.</p>
                                                <Link to="/facilities" className="btn btn-primary">
                                                    <i className="fa fa-plus me-2"></i>Request Your First Facility
                                                </Link>
                                            </div>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Remove Physician Modal */}
            <div className="modal fade" id="removePhysicianModal" tabIndex="-1" aria-labelledby="removePhysicianModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="removePhysicianModalLabel">Remove Physician</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={closeRemoveModal}></button>
                        </div>
                        <div className="modal-body">
                            {selectedPhysician && (
                                <>
                                    <div className="text-center mb-4">
                                        <i className="fa fa-exclamation-triangle fa-3x text-warning mb-3"></i>
                                        <h5>Are you sure you want to remove this physician?</h5>
                                    </div>
                                    
                                    <div className="card mb-3">
                                        <div className="card-body">
                                            <div className="d-flex align-items-center">
                                                <div className="me-3">
                                                    <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center" style={{width: '50px', height: '50px'}}>
                                                        <i className="fa fa-user-md"></i>
                                                    </div>
                                                </div>
                                                <div>
                                                    <h6 className="mb-1">{selectedPhysician.full_name}</h6>
                                                    <p className="mb-1 text-muted">{selectedPhysician.specialty}</p>
                                                    <small className="text-muted">
                                                        {selectedPhysician.hospital}
                                                        {selectedPhysician.npi && ` • NPI: ${selectedPhysician.npi}`}
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="alert alert-warning">
                                        <i className="fa fa-info-circle me-2"></i>
                                        <strong>This action will:</strong>
                                        <ul className="mb-0 mt-2">
                                            <li>Remove the physician from your dashboard</li>
                                            <li>Remove all associated appointments from your calendar</li>
                                            <li>This action cannot be undone</li>
                                        </ul>
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" data-bs-dismiss="modal" onClick={closeRemoveModal}>
                                Cancel
                            </button>
                            <button 
                                type="button" 
                                className="btn btn-danger" 
                                onClick={handleRemovePhysician}
                                disabled={isRemoving}
                            >
                                {isRemoving ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Removing...
                                    </>
                                ) : (
                                    <>
                                        <i className="fa fa-trash me-2"></i>
                                        Remove Physician
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

        </>


    )
};

export default Admin;
